import { useEffect, useMemo, useRef, useState } from "react";
import {
  PAYWALL_IMAGES,
  PAYWALL_VIDEOS,
  posterFor,
  type PaywallVariant,
} from "../lib/paywallVariants";

/** Per-column scroll durations (seconds) — varied so columns drift out of sync. */
const DURATIONS = [38, 50, 42, 56, 45, 60, 48, 52];
/** Tiles stacked per column before the set is duplicated for a seamless loop. */
const TILES_PER_COLUMN = 4;

/** Track the container's width so we can thin the columns on narrow viewports. */
function useContainerWidth(): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) setWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

/**
 * The moving masonry behind the paywall copy: `variant.cols` vertical strips of
 * generated media that scroll in alternating directions, forever. Images paint
 * eagerly; clips autoplay muted. Column count scales down with width so tiles
 * never get slivered on a phone. Decorative — hidden from assistive tech.
 */
export function PaywallMasonry({ variant }: { variant: PaywallVariant }): React.ReactElement {
  const [ref, width] = useContainerWidth();
  const isVideo = variant.kind === "vid";
  const pool = isVideo ? PAYWALL_VIDEOS : PAYWALL_IMAGES;

  // ~150px per column feels right; clamp to [3, variant.cols].
  const cols = width
    ? Math.max(3, Math.min(variant.cols, Math.round(width / 150)))
    : variant.cols;

  const columns = useMemo(() => {
    return Array.from({ length: cols }, (_, c) => {
      const set = Array.from(
        { length: TILES_PER_COLUMN },
        (_, k) => pool[(c * 3 + k) % pool.length],
      );
      return {
        key: c,
        reverse: c % 2 === 1,
        duration: DURATIONS[c % DURATIONS.length],
        // Duplicate the set: translateY(-50%) lands exactly on the seam.
        tiles: [...set, ...set],
      };
    });
  }, [cols, pool]);

  return (
    <div ref={ref} aria-hidden="true" className="absolute inset-0 flex gap-2 overflow-hidden">
      {columns.map((col) => (
        <div
          key={col.key}
          className="flex min-w-0 flex-1 flex-col gap-2 will-change-transform"
          style={{
            animation: `paywall-scroll ${col.duration}s linear infinite`,
            animationDirection: col.reverse ? "reverse" : "normal",
          }}
        >
          {col.tiles.map((src, i) =>
            isVideo ? (
              <video
                key={`${col.key}-${i}`}
                src={src}
                poster={posterFor(src)}
                muted
                loop
                autoPlay
                playsInline
                preload="auto"
                className="block w-full rounded-[10px] object-cover"
              />
            ) : (
              <img
                key={`${col.key}-${i}`}
                src={src}
                alt=""
                loading="eager"
                decoding="async"
                className="block w-full rounded-[10px] object-cover"
              />
            ),
          )}
        </div>
      ))}
    </div>
  );
}
